import {
  CampaignCategory,
  CampaignScheduleType,
  CampaignSummaryFragment,
  UpdateCampaignInput,
  PrTargetAge,
  PrTargetGender,
  CampaignRequirementOptions,
  AgeLimit,
  CampaignSnsRequirementSnsPostType,
  CopyCampaignFragment,
  CampaignSnsRequirementSnsType,
  CreateCampaignInput,
  FormatVersion,
  CampaignCostHandlingMethod,
  ExpenseEvidenceType,
  CampaignReservableWeeklySchedule,
  LanguageType,
  Companion,
} from '~/generated/graphql'
import { z } from 'zod'
import { sponsorServiceNameSchemaFields, validateCampaignNameLength } from './SponsorServiceName'
import { sponsorServicePriceSchemaFields, validateSponsorServicePrice } from './SponsorServicePrice'
import { sponsorServiceUrlSchemaFields } from './SponsorServiceUrl'
import { prTargetGenderSchemaFields } from './PrTargetGender'
import { prTargetAgeSchemaFields } from './PrTargetAge'
import { maxCompanionCountSchemaFields } from './MaxCompanionCount'
import { campaignImageSchemaFields } from './CampaignImage'
import { ageLimitSchemaFields } from './AgeLimit'
import { offerAutoMessageSchemaFields } from './OfferAutoMessage'
import { grossSchema, grossSchemaFields, validateGross } from './Gross'
import { tagsSchemaFields } from './Tags'
import { requestUrlsSchemaFields } from './RequestUrls'
import { snsPostRedirectUrlSchemaFields, validateSnsPostRedirectUrl } from './SnsPostRedirectUrl'
import { guidanceSchemaFields, validateGuidance } from './Guidance'
import { createImage } from '~/lib/covertImages'
import { addSign, removeSign } from '~/lib/feature/campaign'
import { genreAndSubGenreSchemaFields, validateSearchGenre } from './GenreAndSubGenre'
import { snsSchema, snsSchemaFields, validateSns } from './SNS'
import { CompositionOptions } from '~/lib/types/Campaign'
import { decodeId } from '~/lib/convertId'
import { useRegularlyPublishedCampaignSchemaFields } from './UseRegularlyPublishedCampaign'
import { getPossibleWeekDayAndMinutes } from '~/lib/possibleWeekDayAndMinutes'
import { storeInfoSchema } from './StoreInfo'
import { selectCostHandlingMethodSchemaFields } from './SelectCostHandlingMethod'
import { selectExpenseEvidenceTypeSchemaFields } from './SelectExpenseEvidenceType'
import { reservationScheduleSchemaFields } from './type'
import { validateReservationSchedule } from '../campaign_create/ReservationScheduleContents'
import { DateTime } from 'luxon'
import {
  validateExpenseSetting,
  validateInfluencerBurdenRate,
} from '~/features/campaign_create/ExpenseSetting/validation'
import {
  offerStartedSchemaFields,
  PublishType,
  validateOfferStarted,
} from '~/features/campaign_input/OfferStartedDatePicker'
import { desiredLanguagesSchemaFields, validateDesiredLanguages } from '~/features/campaign_input/DesiredLanguages'
import { influencerBurdenRateSchemaFields } from './BurdenRateSelector'
import { toBurdenRate } from './constants/burdenRate'

/**
 * 作成と編集で共通のフィールド
 */
const commonSchemaFields = {
  ...ageLimitSchemaFields,
  ...campaignImageSchemaFields,
  ...grossSchemaFields,
  ...guidanceSchemaFields,
  ...maxCompanionCountSchemaFields,
  ...offerAutoMessageSchemaFields,
  ...prTargetAgeSchemaFields,
  ...prTargetGenderSchemaFields,
  ...sponsorServiceNameSchemaFields,
  ...sponsorServiceUrlSchemaFields,
  ...desiredLanguagesSchemaFields,
  // 明示的な上書き
  ...genreAndSubGenreSchemaFields,
  // gross の sponsorServicePriceIntax の値を上書きするため
  ...sponsorServicePriceSchemaFields,
  ...influencerBurdenRateSchemaFields,
}

/**
 * キャンペーン作成フォームのスキーマを定義する関数
 */
const baseCreateFormSchema = z.object({
  ...commonSchemaFields,
  ...selectCostHandlingMethodSchemaFields,
  ...selectExpenseEvidenceTypeSchemaFields,
  ...snsSchemaFields,
  ...useRegularlyPublishedCampaignSchemaFields,
  ...reservationScheduleSchemaFields,
  ...offerStartedSchemaFields,
  scheduleType: z.nativeEnum(CampaignScheduleType),
})

export const createFormSchema = baseCreateFormSchema
  .and(storeInfoSchema)
  .superRefine(validateSns)
  .superRefine(validateGross)
  .superRefine(validateReservationSchedule)
  .superRefine(validateSponsorServicePrice)
  .superRefine(validateCampaignNameLength)
  .superRefine(validateExpenseSetting)
  .superRefine(validateGuidance)
  .superRefine(validateSearchGenre)
  .superRefine(validateOfferStarted)
  .superRefine(validateDesiredLanguages)
  .superRefine(validateInfluencerBurdenRate)

/**
 * キャンペーン作成フォームの値の型
 */
export type CreateCampaignState = z.input<typeof createFormSchema>

/**
 * キャンペーン編集フォームのスキーマを定義する関数
 */
const baseEditFormSchema = z.object({
  ...commonSchemaFields,
  // edit専用フィールド
  ...tagsSchemaFields,
  ...requestUrlsSchemaFields,
  companion: z.nativeEnum(Companion).nullish(),
  costHandlingMethod: z.nativeEnum(CampaignCostHandlingMethod),
  ...snsPostRedirectUrlSchemaFields,
})

export const editFormSchema = baseEditFormSchema
  .and(storeInfoSchema)
  .superRefine(validateGross)
  .superRefine(validateSnsPostRedirectUrl)
  .superRefine(validateSponsorServicePrice)
  .superRefine(validateCampaignNameLength)
  .superRefine(validateGuidance)
  .superRefine(validateSearchGenre)
  .superRefine(validateDesiredLanguages)
  .superRefine(validateInfluencerBurdenRate)

/**
 * キャンペーン編集フォームの値の型
 */
export type EditCampaignState = z.input<typeof editFormSchema>

export function createSnsRequirementDefaultValues(): z.infer<typeof snsSchema>['snsRequirements'] {
  return {
    INSTAGRAM: {
      checked: true,
      tagList: [
        {
          languageType: LanguageType.Japanese,
          tags: [],
        },
        {
          languageType: LanguageType.English,
          tags: [],
        },
      ],
      requestUrl: '',
      snsPostTypeList: {
        [CampaignSnsRequirementSnsPostType.InstagramFeed]: true,
        [CampaignSnsRequirementSnsPostType.InstagramReel]: true,
        [CampaignSnsRequirementSnsPostType.InstagramStories]: false,
      },
    },
    TIKTOK: {
      checked: true,
      tagList: [
        {
          languageType: LanguageType.Japanese,
          tags: [],
        },
        {
          languageType: LanguageType.English,
          tags: [],
        },
      ],
      requestUrl: '',
    },
  }
}

// 初期値
export const initialCampaignRequirementOptions = {
  [CampaignRequirementOptions.DraftCheck]: false,
  [CampaignRequirementOptions.SecondaryUse]: false,
  [CampaignRequirementOptions.SpecifiedPrText]: false,
  COMPOSITION_SPECIFICATION: false,
}

// キャンペーン作成初期値
export const createCampaignInitialState: CreateCampaignState = {
  category: null,
  shopName: '',
  shopPostalCode: '',
  shopPref: '',
  cityName: '',
  streetAddress: '',
  buildingName: null,
  nearestStationName: '',
  shopTel: '',
  name: '',
  searchGenre: '',
  searchSubGenreIds: [],
  sponsorServicePriceIntax: 0,
  coverImages: [],
  url: '',
  prTargetAge: [
    PrTargetAge.Age_17AndUnder,
    PrTargetAge.Age_18To_24,
    PrTargetAge.Age_25To_34,
    PrTargetAge.Age_35To_44,
    PrTargetAge.Age_45To_54,
    PrTargetAge.Age_55AndAbove,
  ],
  prTargetGender: PrTargetGender.FemaleAndMale,
  ageLimit: AgeLimit.NoLimit,
  maxCompanionCount: 0,
  snsRequirements: createSnsRequirementDefaultValues(),
  guidance: '',
  offerAutoMessage: '',
  basicGross: 0,
  campaignRequirementOptions: initialCampaignRequirementOptions,
  campaignRequirementOptionSpecifiedPrText: '',
  campaignRequirementCompositionOptions: undefined,
  gross: 0,
  useRegularlyPublishedCampaign: false,
  shopTemplateId: null,
  scheduleType: CampaignScheduleType.NotUsed,
  schedules: {},
  costHandlingMethod: CampaignCostHandlingMethod.ExpenseCampaign,
  influencerBurdenRate: 0,
  expenseEvidenceType: ExpenseEvidenceType.Receipt,
  offerStarted: null,
  publishType: PublishType.Immediate,
  desiredLanguages: [LanguageType.Japanese],
}

const mapCampaignOptions = (selectedOptions: readonly CampaignRequirementOptions[]) => {
  return {
    DRAFT_CHECK: selectedOptions.includes(CampaignRequirementOptions.DraftCheck),
    SECONDARY_USE: selectedOptions.includes(CampaignRequirementOptions.SecondaryUse),
    SPECIFIED_PR_TEXT: selectedOptions.includes(CampaignRequirementOptions.SpecifiedPrText),
    COMPOSITION_SPECIFICATION: CompositionOptions.some((opt) => selectedOptions.includes(opt)),
  }
}

const convertInstagramPostTypeList = (selected: readonly CampaignSnsRequirementSnsPostType[]) =>
  [
    CampaignSnsRequirementSnsPostType.InstagramFeed,
    CampaignSnsRequirementSnsPostType.InstagramReel,
    CampaignSnsRequirementSnsPostType.InstagramStories,
  ].reduce(
    (acc, type) => {
      acc[type] = selected.includes(type)
      return acc
    },
    {} as Record<CampaignSnsRequirementSnsPostType, boolean>,
  )

const mapCampaignSnsRequirements = (campaignSnsRequirements: CopyCampaignFragment['campaignSnsRequirements']) => {
  const initialValues = createSnsRequirementDefaultValues()

  initialValues.INSTAGRAM.checked = false
  initialValues.TIKTOK.checked = false

  for (const req of campaignSnsRequirements) {
    if (req.snsType === CampaignSnsRequirementSnsType.Instagram) {
      initialValues.INSTAGRAM.checked = true
      initialValues.INSTAGRAM.tagList = req.tagList.map((tag) => ({
        languageType: tag.languageType,
        tags: tag.tags.slice(),
      }))
      initialValues.INSTAGRAM.requestUrl = removeSign(req.requestUrl)
      initialValues.INSTAGRAM.snsPostRedirectUrl = req.snsPostRedirectUrl ?? ''
      initialValues.INSTAGRAM.snsPostTypeList = convertInstagramPostTypeList(req.snsPostTypeList)
    } else if (req.snsType === CampaignSnsRequirementSnsType.Tiktok) {
      initialValues.TIKTOK.checked = true
      initialValues.TIKTOK.tagList = req.tagList.map((tag) => ({
        languageType: tag.languageType,
        tags: tag.tags.slice(),
      }))
      initialValues.TIKTOK.requestUrl = removeSign(req.requestUrl)
    }
  }
  return initialValues
}

export const mapReservableWeeklySchedule = (reservableWeeklySchedule: readonly CampaignReservableWeeklySchedule[]) => {
  return reservableWeeklySchedule.reduce(
    (acc, schedule) => {
      const { weekDay, possibleMinutes } = schedule

      const time = DateTime.fromObject({
        hour: Math.floor(possibleMinutes / 60),
        minute: possibleMinutes % 60,
      }).toFormat('H:mm')

      const key = `${weekDay}_${time}`
      acc[key] = true
      return acc
    },
    {} as Record<string, boolean>,
  )
}

/**
 * サーバー送信時にオプション指定から構図に関するものを取り除く
 */
function filterCompositionOption(
  params: Pick<
    z.infer<typeof grossSchema>,
    'campaignRequirementOptions' | 'campaignRequirementCompositionOptions' | 'campaignRequirementOptionSpecifiedPrText'
  >,
) {
  const hasCompositionSpecification = params.campaignRequirementOptions.COMPOSITION_SPECIFICATION
  const campaignRequirementOptionList = Object.entries(params.campaignRequirementOptions)
    .filter(([key, value]) => value === true && key !== 'COMPOSITION_SPECIFICATION')
    .map(([key]) => key as CampaignRequirementOptions)
  if (hasCompositionSpecification && params.campaignRequirementCompositionOptions) {
    campaignRequirementOptionList.push(params.campaignRequirementCompositionOptions as CampaignRequirementOptions)
  }

  return {
    campaignRequirementOptions: campaignRequirementOptionList,
    campaignRequirementOptionSpecifiedPrText: params.campaignRequirementOptions[
      CampaignRequirementOptions.SpecifiedPrText
    ]
      ? params.campaignRequirementOptionSpecifiedPrText
      : undefined,
  }
}

/**
 * サーバーから取得したキャンペーン情報を CreateCampaignState に変換する
 */
export const convertCampaignToCreateCampaignState = (campaign: CopyCampaignFragment): CreateCampaignState => {
  return {
    ...createCampaignInitialState,
    cityName: campaign.location?.cityName ?? '',
    streetAddress: campaign.location?.streetAddress ?? '',
    buildingName: campaign.location?.buildingName ?? null,
    shopTel: campaign.location?.tel ?? '',
    nearestStationName: campaign.location?.stationName ?? '',
    shopName: campaign.location?.name ?? '',
    shopPostalCode: campaign.location?.postalCode ?? '',
    shopPref: campaign.location?.prefectureCode ?? '',
    sponsorServicePriceIntax: campaign.sponsorServicePriceIntax ?? 0,
    name: campaign.name,
    url: campaign.url ?? '',
    ageLimit: campaign.ageLimit ?? AgeLimit.NoLimit,
    category: campaign.category === 'EVENT' ? null : campaign.category,
    snsRequirements: mapCampaignSnsRequirements(campaign.campaignSnsRequirements),
    scheduleType: campaign.scheduleType,
    coverImages: campaign.coverImageUrls
      .map((e) => e.original)
      .map((e) => ({ dataURL: e }))
      .filter((e) => e.dataURL),

    // v4
    searchGenre: campaign.companySelectSearchGenre ?? createCampaignInitialState.searchGenre,
    searchSubGenreIds: campaign.subGenre.map((subGenre) => decodeId(subGenre).id),
    basicGross: campaign.basicGross ?? createCampaignInitialState.basicGross,
    campaignRequirementOptionSpecifiedPrText:
      campaign.campaignRequirementOptionSpecifiedPrText ??
      createCampaignInitialState.campaignRequirementOptionSpecifiedPrText,
    campaignRequirementOptions: mapCampaignOptions(campaign.campaignRequirementOptions),
    campaignRequirementCompositionOptions:
      CompositionOptions.find((option) => campaign.campaignRequirementOptions.includes(option)) ??
      createCampaignInitialState.campaignRequirementCompositionOptions,
    guidance: campaign.guidance ?? createCampaignInitialState.guidance,
    maxCompanionCount: campaign.maxCompanionCount ?? createCampaignInitialState.maxCompanionCount,
    prTargetAge: campaign.prTargetAge ?? createCampaignInitialState.prTargetAge,
    prTargetGender: campaign.prTargetGender ?? createCampaignInitialState.prTargetGender,
    costHandlingMethod: campaign.costHandlingMethod ?? createCampaignInitialState.costHandlingMethod,
    influencerBurdenRate: toBurdenRate(campaign.influencerBurdenRate),
    expenseEvidenceType: campaign.expenseEvidenceType,
    schedules: mapReservableWeeklySchedule(campaign.reservableWeeklySchedule),
    desiredLanguages: [...campaign.desiredLanguages],
  }
}

/**
 * キャンペーンをCampaignEditFieldValuesに変換する
 */
export const convertCampaignToEditCampaignState = (
  campaign: NonNullable<CampaignSummaryFragment>,
): EditCampaignState => {
  const instagram = campaign.campaignSnsRequirements.find((e) => e.snsType === CampaignSnsRequirementSnsType.Instagram)
  if (campaign.formatVersion !== FormatVersion.V4Campaign) {
    throw new Error('Invalid format version')
  }
  if (campaign.costHandlingMethod === null || campaign.costHandlingMethod === undefined)
    throw new Error('Invalid cost handling method')
  return {
    category: campaign.category === 'EVENT' ? null : campaign.category,
    coverImages: campaign.coverImageUrls.map((e) => ({
      dataURL: e.original,
    })),
    name: campaign.name,
    searchGenre: campaign.companySelectSearchGenre ?? '',
    searchSubGenreIds: campaign.subGenre.map((subGenre) => decodeId(subGenre).id),

    url: campaign.url ?? createCampaignInitialState.url,
    sponsorServicePriceIntax: campaign.sponsorServicePriceIntax ?? 0,

    prTargetAge: campaign.prTargetAge ?? createCampaignInitialState.prTargetAge,
    prTargetGender: campaign.prTargetGender ?? createCampaignInitialState.prTargetGender,
    ageLimit: campaign.ageLimit ?? AgeLimit.NoLimit,

    guidance: campaign.guidance ?? createCampaignInitialState.guidance,

    campaignSnsRequirements: campaign.campaignSnsRequirements.map((e) => ({
      snsType: e.snsType,
      tagList: e.tagList.map((tag) => ({
        languageType: tag.languageType,
        tags: [...tag.tags],
      })),
      requestUrl: removeSign(e.requestUrl),
      snsPostTypeList: [...e.snsPostTypeList],
    })),
    snsPostRedirectUrl: instagram?.snsPostRedirectUrl ?? '',
    instagramStoriesEnabled: !!campaign.campaignSnsRequirements.find(
      (e) =>
        e.snsType === CampaignSnsRequirementSnsType.Instagram &&
        e.snsPostTypeList.includes(CampaignSnsRequirementSnsPostType.InstagramStories),
    ),

    gross: campaign.gross ?? 0,
    basicGross: campaign.basicGross ?? 0,
    campaignRequirementOptionSpecifiedPrText: campaign.campaignRequirementOptionSpecifiedPrText ?? undefined,
    campaignRequirementOptions: mapCampaignOptions(campaign.campaignRequirementOptions),
    campaignRequirementCompositionOptions: CompositionOptions.find((option) =>
      campaign.campaignRequirementOptions.includes(option),
    ),

    maxCompanionCount: campaign.maxCompanionCount ?? 0,

    offerAutoMessage: campaign.offerAutoMessage ?? '',

    shopPref: campaign.location?.prefectureCode ?? '',
    cityName: campaign.location?.cityName ?? '',
    streetAddress: campaign.location?.streetAddress ?? '',
    buildingName: campaign.location?.buildingName ?? null,
    shopName: campaign.location?.name ?? '',
    nearestStationName: campaign.location?.stationName ?? '',
    shopPostalCode: campaign.location?.postalCode ?? '',
    shopTel: campaign.location?.tel ?? '',
    shopTemplateId: campaign.location?.id ? decodeId(campaign.location.id).id : null,

    costHandlingMethod: campaign.costHandlingMethod,
    desiredLanguages: [...campaign.desiredLanguages],
    influencerBurdenRate: toBurdenRate(campaign.influencerBurdenRate),
  }
}

export function mapToUpdateCampaignInput(
  data: z.infer<typeof snsSchema>['snsRequirements'],
  desiredLanguages: LanguageType[],
) {
  return {
    snsRequirements: Object.entries(data)
      .filter(([, sns]) => sns.checked)
      .map(([snsType, sns]) => {
        const postTypeList =
          'snsPostTypeList' in sns
            ? (Object.entries(sns.snsPostTypeList)
                .filter(([, value]) => value === true)
                .map(([key]) => key) as [])
            : []

        return {
          requestUrl: addSign(sns.requestUrl),
          snsPostRedirectUrl: 'snsPostRedirectUrl' in sns ? (sns.snsPostRedirectUrl ?? '') : '',
          snsPostTypeList: postTypeList.length > 0 ? postTypeList : [CampaignSnsRequirementSnsPostType.SnsDefault],
          snsType: snsType as CampaignSnsRequirementSnsType,
          tagList: sns.tagList
            .filter((tag) => desiredLanguages.includes(tag.languageType))
            .map((tag) => ({
              languageType: tag.languageType,
              tags: tag.tags,
            })),
        }
      }),
  }
}

/**
 * CreateCampaignState を作成用パラメータに変換する
 */
export const convertCreateCampaignStateToCampaignCreateParam = async (
  campaign: CreateCampaignState,
): Promise<CreateCampaignInput> => {
  const possibleWeekDayAndMinutes = getPossibleWeekDayAndMinutes(campaign.schedules ?? {})

  // 作成パラメータに設定できないフィールドはここで除去する
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    gross,
    coverImages,
    campaignRequirementCompositionOptions,
    campaignRequirementOptionSpecifiedPrText,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    schedules,
    costHandlingMethod,
    expenseEvidenceType,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    publishType,
    ...data
  } = createFormSchema.parse(campaign)
  const imageList = await createImage(coverImages)
  const options = filterCompositionOption({
    campaignRequirementOptions: data.campaignRequirementOptions,
    campaignRequirementCompositionOptions,
    campaignRequirementOptionSpecifiedPrText,
  })
  const offerStarted = data.offerStarted ? DateTime.fromJSDate(data.offerStarted).setZone('Asia/Tokyo').toISO() : null

  const desiredLanguages = data.category === CampaignCategory.Product ? [LanguageType.Japanese] : data.desiredLanguages

  return {
    ...data,
    coverImages: imageList,
    snsRequirements: mapToUpdateCampaignInput(data.snsRequirements, desiredLanguages).snsRequirements,
    campaignRequirementOptions: options.campaignRequirementOptions,
    campaignRequirementOptionSpecifiedPrText: options.campaignRequirementOptionSpecifiedPrText,
    campaignReservableWeeklySchedules: possibleWeekDayAndMinutes,
    costHandlingMethod,
    expenseEvidenceType:
      costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign
        ? expenseEvidenceType
        : ExpenseEvidenceType.NoEvidence,
    offerStarted: offerStarted,
    desiredLanguages: data.category === CampaignCategory.Product ? [LanguageType.Japanese] : data.desiredLanguages,
    buildingName: data.buildingName?.trim() || null,
    nearestStationName:
      data.nearestStationName && !data.nearestStationName.endsWith('駅')
        ? data.nearestStationName + '駅'
        : data.nearestStationName,
  }
}

/**
 * EditCampaignState を更新用パラメーターに変換する
 */
export const convertEditCampaignStateToCampaignUpdateParam = async (
  fieldValues: EditCampaignState,
): Promise<Omit<UpdateCampaignInput, 'id'>> => {
  const parsedData = editFormSchema.parse(fieldValues)
  // 編集パラメータに設定できないフィールドはここで除去する
  const {
    coverImages,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    category,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    searchGenre,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    instagramStoriesEnabled,
    campaignSnsRequirements,
    campaignRequirementCompositionOptions,
    campaignRequirementOptionSpecifiedPrText,
    snsPostRedirectUrl,
    // V3 パラメータは v3 の時に undefined に変換して値が更新されないようにする
    searchSubGenreIds,
    maxCompanionCount,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    costHandlingMethod,
    ...data
  } = parsedData
  const images = await createImage(coverImages)
  const options = filterCompositionOption({
    campaignRequirementOptions: data.campaignRequirementOptions,
    campaignRequirementCompositionOptions,
    campaignRequirementOptionSpecifiedPrText,
  })
  return {
    ...data,
    coverImages: images,
    snsRequirements: campaignSnsRequirements.map((e) => ({
      requestUrl: addSign(e.requestUrl),
      snsType: e.snsType,
      tagList: e.tagList
        .filter((tag) => data.desiredLanguages.includes(tag.languageType))
        .map((tag) => ({
          languageType: tag.languageType,
          tags: tag.tags,
        })),
      snsPostTypeList: e.snsPostTypeList,
      snsPostRedirectUrl: (e.snsType === 'INSTAGRAM' && snsPostRedirectUrl) || '',
    })),
    campaignRequirementOptions: options.campaignRequirementOptions,
    campaignRequirementOptionSpecifiedPrText: options.campaignRequirementOptionSpecifiedPrText,
    searchSubGenreIds: searchSubGenreIds,
    maxCompanionCount: maxCompanionCount,
    buildingName: data.buildingName?.trim() || null,
    nearestStationName:
      data.nearestStationName && !data.nearestStationName.endsWith('駅')
        ? data.nearestStationName + '駅'
        : data.nearestStationName,
  }
}
